import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { CheckBox } from "@mui/icons-material";
import { useRouteLoaderData } from "react-router-dom";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import { formatAmount } from "../../../../../HelperFunctions/HelperFunctions";

const columns = [
  { name: "Sale Details", dataKey: "sale_date", width: 250 },
  { name: "Card Type", dataKey: "card_type", width: 100 },
  { name: "Deposit Date", dataKey: "settlement_date", width: 100 },
  { name: "Deposit Amount", dataKey: "gross_amount", width: 100 },
  { name: "Chargebacks", dataKey: "chargebacks", width: 100 },
  { name: "Reject / Adj", dataKey: "financial_adjustments", width: 100 },
  // { name: 'Settlement Fees', dataKey: 'financial_adjustments', width: 100 },
  { name: "Net Deposit", dataKey: "net_amount", width: 100 },
  // { name: 'Open / Difference', dataKey: 'openDifference', width: 100 }
  { name: "Open / Difference", dataKey: "difference", width: 100 },
];
const RenderSaleDetails = ({ data }) => {
  return (

    <Stack direction="row" width="100%" height="100%" >
      <Stack
        direction="column"
        width="70%"
        justifyContent="space-between"
      >
        {data.sales?.map((sale, index) => (
          <Stack
            key={index}
            p={1.5}
            direction="row"
            justifyContent="space-between"
          >
            <Stack gap={1}>
              <Typography fontSize="11px" fontWeight={500}>
                {sale?.sale_date}
              </Typography>
              <Typography fontSize="11px" fontWeight={500}>
                {sale?.card_type}
              </Typography>
            </Stack>

            <Stack justifyContent="center" >
              <Typography align="center" fontWeight={600}>
                ${formatAmount(sale?.amount)}
              </Typography>
            </Stack>

          </Stack>
        ))}
      </Stack>

      <Stack
        width="30%"
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Chip
          sx={{
            borderRadius: 1,
            backgroundColor: "#cbe8c7",
            fontSize: "9px",
            fontWeight: 700,
            color: "#0e7634",
            px: 1,
            "& .MuiChip-icon": {
              color: "#0e7634",
              fontSize: "13px",
            },
          }}
          icon={<CheckBox />}
          label="Matched"
          size="small"
        />
      </Stack>
    </Stack>
  );
};

const Transactions = ({ data, field }) => (
  <Box width="100%">
    {data.map((item, index) =>
      field === "chargebacks" ? (
        <>
          <Stack px={2} py={"5px"}>
            <Chip
              sx={{
                borderRadius: 1,
                backgroundColor: "#f8ced3",
                fontSize: "11px",
                color: "#D9001B",
                px: 1,
                "& .MuiChip-icon": {
                  color: "#D9001B",
                  fontSize: "13px",
                },
              }}
              // label={
              //   item[field] !== undefined && item[field] !== null
              //     ? item[field]
              //     : "-"
              // }
              label={
                item[field] !== undefined && item[field] !== null
                  ? formatAmount(item[field])
                  : "-"
              }
              size="small"
            />
          </Stack>
          {/* {index < data.length - 1 && <Divider />} */}
        </>
      ) : (
        <Box key={index}>
          <Typography
            fontSize={12}
            fontWeigh={500}
            color="#626262"
            px={2}
            py={1}
          >
            {item[field] !== undefined && item[field] !== null
              ? formatAmount(item[field])
              : "-"}
          </Typography>
          {/* {index < data.length - 1 && <Divider />} */}
        </Box>
      ),
    )}
  </Box>
);

const MatchedAmountTable = ({
  data,
  page: externalPage,
  setPage: setExternalPage,
}) => {
  // const { rowsPerPage } = useRouteLoaderData("reconciliation");
  const rowsPerPage = 40;

  const tableContainerRef = useRef(null);
  const [page, setPage] = useState(externalPage ?? 0);

  // Update external page state when internal page changes
  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      if (setExternalPage) {
        setExternalPage(newPage);
      }
    },
    [setExternalPage],
  );

  // Sync internal page state with external page prop
  useEffect(() => {
    if (externalPage !== undefined && externalPage !== page) {
      setPage(externalPage);
    }
  }, [externalPage, page]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(data.length / rowsPerPage) - 1);
    const currentPage = externalPage !== undefined ? externalPage : page;
    const newPage = currentPage > maxPage ? maxPage : currentPage;
    if (newPage !== currentPage) {
      handlePageChange(newPage);
    }
  }, [data.length, rowsPerPage, externalPage, page, handlePageChange]);

  const total = data.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const paginatedData = data.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  return (
    <>
      <TableContainer ref={tableContainerRef} sx={{ mt: 1, height: "340px" }}>
        <Table stickyHeader size="small" 
        sx={{
          borderCollapse: "separate",
          borderSpacing: "0 12px",
        }}
        >
          <TableHead sx={{ background: "#eee" }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.dataKey}
                  sx={{
                    backgroundColor: "#d7d7d7",
                    borderRight: "1px solid white",
                    fontSize: 13,
                    maxWidth: col.width,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {col.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 4}
                  align="center"
                  sx={{ fontSize: 14, color: "#999" }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={{
                  backgroundColor: "#fff",
                  boxShadow: "0px 0px 10px rgba(0,0,0,0.10)",
                  "& td": { border: 0 },
                  // transition: "all 0.2s ease-in-out",
                  // "&:hover": {
                  //   boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                  //   transform: "translateY(-3px)",
                  //   backgroundColor: "#fafafa",
                  // },
                }}>
                  {columns.map((col, colIndex) => {
                    return (
                      <TableCell
                        key={colIndex}
                        sx={{
                          maxWidth: col.width,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          p: 0,
                          ...(colIndex === 0 && {
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                          }),
                          ...(colIndex === columns.length - 1 && {
                            borderTopRightRadius: 8,
                            borderBottomRightRadius: 8,
                          }),
                        }}
                      >
                        {col.dataKey === "sale_date" ? (
                          <RenderSaleDetails data={row} />
                        ) : col.dataKey === "difference" ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            
                          >
                            <Chip
                              sx={{
                                borderRadius: 1,
                                backgroundColor: "#f8ced3",
                                fontSize: "11px",
                                color: "#D9001B",
                                px: 1,
                              }}
                              // label={row.difference ?? "-"}
                              label={
                                row.difference != null
                                  ? formatAmount(row.difference)
                                  : "-"
                              }
                              size="small"
                            />
                          </Box>
                        ) : (
                          <Transactions
                            data={row.transactions}
                            field={col.dataKey}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            p: 1,
            gap: 1,
          }}
        >
          <Typography fontSize={"12px"}>Page</Typography>

          <button
            onClick={() => {
              if (page > 0) {
                handlePageChange(page - 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page === 0}
            style={{
              fontSize: "10px",
              cursor: page === 0 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowLeftOutlinedIcon fontSize="small" />
          </button>

          <input
            type="text"
            defaultValue={page + 1}
            key={page}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const value = e.target.value.trim();
                let newPage;
                if (value === "" || value === "0") {
                  newPage = 0;
                } else {
                  const parsedPage = parseInt(value);
                  if (
                    !isNaN(parsedPage) &&
                    parsedPage >= 1 &&
                    parsedPage <= totalPages
                  ) {
                    newPage = parsedPage - 1;
                  } else if (parsedPage > totalPages) {
                    newPage = totalPages - 1;
                  } else {
                    newPage = 0;
                  }
                }
                handlePageChange(newPage);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
                e.target.blur();
              }
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            style={{
              width: "50px",
              padding: "4px",
              fontSize: "12px",
              textAlign: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <button
            onClick={() => {
              if (page < totalPages - 1) {
                handlePageChange(page + 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page >= totalPages - 1}
            style={{
              // padding: '4px 8px',
              fontSize: "10px",
              cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowRightOutlinedIcon fontSize="small" />
          </button>

          <span style={{ fontSize: "14px" }}>of {totalPages}</span>
        </Box>
      </Stack>
    </>
  );
};

export default MatchedAmountTable;
